"use client";

import { X } from "lucide-react";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import {
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT, SPRING_PANEL } from "../../lib/ease";
import { cn } from "../../lib/utils";

type ModalContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerId: string;
  contentId: string;
};

const ModalContext =
  createContext<ModalContextValue | null>(null);

function useModalContext(component: string) {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(`${component} must be used within <Modal>`);
  }
  return context;
}

export interface ModalProps {
  children: ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Initial state when used uncontrolled. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A modal whose full-size surface unfolds outward from its exact center.
 * Supports controlled and uncontrolled state through composable primitives.
 */
export function Modal({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
}: ModalProps) {
  const id = useId();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  const value = useMemo<ModalContextValue>(
    () => ({
      open,
      setOpen,
      triggerId: `${id}-trigger`,
      contentId: `${id}-content`,
    }),
    [id, open, setOpen],
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

export interface ModalTriggerProps {
  children: ReactElement;
}

/** Wraps one interactive element and opens or closes the modal. */
export function ModalTrigger({
  children,
}: ModalTriggerProps) {
  const context = useModalContext("ModalTrigger");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    id: context.triggerId,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(!context.open);
    },
    "aria-haspopup": "dialog",
    "aria-expanded": context.open,
    "aria-controls": context.open ? context.contentId : undefined,
  });
}

export interface ModalCloseProps {
  children: ReactElement;
}

/** Wraps one interactive element and closes the modal. */
export function ModalClose({
  children,
}: ModalCloseProps) {
  const context = useModalContext("ModalClose");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | undefined;

  return cloneElement(child, {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      childOnClick?.(event);
      if (!event.defaultPrevented) context.setOpen(false);
    },
  });
}

export interface ModalContentProps {
  children: ReactNode;
  /** Accessible name announced by screen readers. */
  ariaLabel: string;
  /** Optional id of descriptive content inside the modal. */
  ariaDescribedBy?: string;
  /** Close on Escape or backdrop press. Default true. */
  dismissible?: boolean;
  /** Render the close control inside the panel's top-right corner. Default true. */
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  className?: string;
  backdropClassName?: string;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * The modal enters the way every other v2 overlay does: opacity plus a small
 * scale, on the shared panel spring.
 *
 * It used to unfold via `clip-path: inset(48% …)` → `inset(0% …)`, which made it
 * the only surface in the set that morphed while popover, tooltip, toast and
 * bottom sheet all faded. That inconsistency was visible whenever two overlays
 * appeared in the same flow — a row menu opening a rename dialog, for instance.
 */
const PANEL_ENTER = { opacity: 1, scale: 1 } as const;
const PANEL_EXIT = { opacity: 0, scale: 0.97 } as const;
const PANEL_INITIAL = { opacity: 0, scale: 0.96 } as const;

function getFocusableElements(root: HTMLElement | null) {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => element.tabIndex >= 0);
}

function PresencePointerGate({
  children,
}: {
  children: (isPresent: boolean) => ReactNode;
}) {
  return children(useIsPresent());
}

export function ModalContent({
  children,
  ariaLabel,
  ariaDescribedBy,
  dismissible = true,
  showCloseButton = true,
  closeButtonLabel = "Close modal",
  className,
  backdropClassName,
}: ModalContentProps) {
  const context = useModalContext("ModalContent");
  const reduce = useReducedMotion() ?? false;
  const [mounted, setMounted] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!context.open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = requestAnimationFrame(() => {
      const [firstFocusable] = getFocusableElements(overlayRef.current);
      (firstFocusable ?? panelRef.current)?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissible) {
        event.preventDefault();
        context.setOpen(false);
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = getFocusableElements(overlayRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [context, dismissible]);

  // Return focus to whatever opened the modal, on close only. Keyed on
  // `context.open` (a boolean) rather than the whole context so an unstable
  // context identity from callers (an inline onOpenChange) cannot re-fire this
  // every render and steal focus from a field mid-interaction. ModalTrigger
  // stamps a triggerId; controlled modals fall back to the captured invoker.
  useEffect(() => {
    if (!context.open) return;
    const invoker = document.activeElement as HTMLElement | null;
    return () => {
      (document.getElementById(context.triggerId) ?? invoker)?.focus();
    };
  }, [context.open, context.triggerId]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {context.open ? (
        <PresencePointerGate>
          {(isPresent) => (
            <div
              ref={overlayRef}
              className="pointer-events-none fixed inset-0 z-[100]"
            >
          <motion.button
            type="button"
            aria-label="Dismiss modal"
            tabIndex={-1}
            disabled={!dismissible}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ pointerEvents: isPresent ? "auto" : "none" }}
            transition={{
              duration: reduce ? 0.1 : 0.28,
              ease: EASE_OUT,
            }}
            onClick={() => context.setOpen(false)}
            className={cn(
              "pointer-events-auto absolute inset-0 h-full w-full cursor-default bg-v2-background/10 backdrop-blur-sm",
              backdropClassName,
            )}
          />

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-y-auto p-4 drop-shadow-2xl">
            {/* Drop-shadow reads the clipped child's alpha, so depth follows the
                unfolding silhouette without introducing another panel layer. */}
            <div className="flex w-full flex-col items-center py-8">
              <motion.div
                ref={panelRef}
                id={context.contentId}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                tabIndex={-1}
                initial={reduce ? { opacity: 0 } : PANEL_INITIAL}
                animate={reduce ? { opacity: 1 } : PANEL_ENTER}
                exit={reduce ? { opacity: 0 } : PANEL_EXIT}
                style={{ pointerEvents: isPresent ? "auto" : "none" }}
                transition={reduce ? { duration: 0.14, ease: EASE_OUT } : SPRING_PANEL}
                className={cn(
                  "pointer-events-auto relative w-full max-w-[26rem] origin-center overflow-hidden rounded-v2-xl border border-v2-border bg-v2-background will-change-[opacity,transform]",
                  className,
                )}
              >
                {children}

                {showCloseButton ? (
                  <motion.button
                    type="button"
                    aria-label={closeButtonLabel}
                    onClick={() => context.setOpen(false)}
                    initial={
                      reduce
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.8 }
                    }
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: reduce ? 1 : 0.88,
                      transition: { duration: 0.1, ease: EASE_OUT },
                    }}
                    transition={{
                      delay: reduce ? 0 : 0.16,
                      duration: reduce ? 0.12 : 0.2,
                      ease: EASE_OUT,
                    }}
                    className="absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-v2-md bg-v2-foreground/[0.05] text-v2-muted-foreground transition-colors hover:bg-v2-foreground/[0.08] hover:text-v2-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </motion.button>
                ) : null}
              </motion.div>
            </div>
          </div>
            </div>
          )}
        </PresencePointerGate>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
